import React, { useState } from "react";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { toast } from "react-toastify";
import { modal, formatDatetime } from "../../../../util";
import { Link } from "react-router-dom";
import DragAndDropTable from "../../../common/DragAndDropTable";
import styled from "styled-components";

const useStyles = makeStyles((theme) => ({
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  text: {
    textDecoration: "none",
    color: "#306F9D",
  },
  inactive: {
    color: "#999999",
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
    background: "#ffffff",
    paddingTop: "10px",
    paddingBottom: "10px",
    minHeight: "800px",
  },
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  button: {
    backgroundColor: "#306F9D",
    textTransform: "capitalize",
    color: "white",
    "&:hover": {
      backgroundColor: "#306F9D",
    },
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
  sortableHeader: {
    cursor: "pointer",
  },
}));

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const statuses = Object.freeze({
  all: null,
  active: "ACTIVE",
  inactive: "INACTIVE",
});

export default function ShippingListingPage(props) {
  const { disabled, match } = props;
  const classes = useStyles();
  const [globalFilter, setGlobalFilter] = useState("");
  const [isSearched, setIsSearched] = useState(false);
  const [orderingDisabled, setOrderingDisabled] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchString, setSearchString] = useState("");
  const { setLoading } = useLoading();
  const [pageSize, setPageSize] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [status, setStatus] = React.useState("all");
  const [elementCounts, setElementCounts] = React.useState({
      all: 0,
      active: 0,
      inactive: 0,
  });


  const getShipping = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getShippings(
        null,
        pageIndex,
        (pageIndex + 1) * pageSize,
        pageSize,
        searchString,
        false,
        statuses[status]
      );
      console.log(result);
      
      await apiHelper.hkiff.getShippings(
        null,
        null,
        null,
        999,
        null,
      ).then((response) => {
        setTotalElements(response.data.totalElements);
        const all = response.data.totalElements;
        const active = response.data.content
            .filter((x) => x.status === "ACTIVE").length;
  
        const inactive = response.data.content
            .filter((x) => x.status === "INACTIVE").length;
        setElementCounts({
            all, active, inactive
        });
      });

      setRows(result.data.content.filter((x) => x));
      setTotalElements(result.data.totalElements);
      setTotalPages(result.data.totalPages);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, searchString, status, setLoading]);

  React.useEffect(() => {
    if (pageSize) getShipping();
  }, [getShipping]);

  const handleSorting = (bool) => {
    if (!isSearched) {
      setOrderingDisabled(bool);
    }
  };

  const handleSearch = (searchString) => {
    setGlobalFilter(searchString);
    setIsSearched(true);
    setOrderingDisabled(true);
  };

  const handleDelete = async (id) => {
    const decision = await modal.fire({
      title: "Delete Shipping Region",
      text: "Are you sure to delete this Shipping Region?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        await apiHelper.hkiff.deleteShippingById(id);
        toast.dark("Shipping Region deleted.");
        await getShipping();
      } catch (err) {
        console.log(err);
      }
    }
  };

  const bulkDelete = (ids) => {
    const promises = ids.map((id) => apiHelper.hkiff.deleteShippingById(id));
    return Promise.all(promises);
  };

  const saveOrdering = async () => {
    try {
        const seqData = {};
        const activeRows = rows.filter((x) => x.status === "ACTIVE")
        activeRows.forEach((e, i) => {
          if (e.status === "ACTIVE") {
            seqData[e.id] = 1 + i;
          }
        });
        console.log(seqData)
        
        if (Object.keys(seqData).length === 0) {
          return toast.error("No active shipping region to save.");
        }
        
        const result = await apiHelper.hkiff.orderShippings(seqData);
        if (result.data.code === 0) {
            toast.dark("Your changes is saved.");
            await getShipping();
        }
    } catch (err) {
        console.log(err);
    }
  };

  const statusFilterHandler = (targetStatus) => () => {
    setLoading(true);
    setStatus(targetStatus);
    setPageIndex(0)
};

  const columns = React.useMemo(
    () => [
      {
        Header: "Country Code",
        accessor: "countryCode",
        disableSortBy: true,
      },
      {
        Header: "Region Name (English)",
        accessor: "localizations.en.name",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
            >
              {row.original.localizations.en.name}
            </Link>
          )
        },
      },
      {
        Header: "Region Name (Traditional Chinese)",
        accessor: "localizations.tc.name",
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
            >
              {row.original.localizations.tc.name}
            </Link>
          );
        },
      },
      {
        Header: "per Order Price",
        accessor: "perOrderPrice",
        disableGlobalFilter: true,
      },
      {
        Header: "per Item Price",
        accessor: "perItemPrice",
        disableGlobalFilter: true,
      },
      {
        Header: "Status",
        accessor: "status",
        disableSortBy: true,
        Cell: ({ row }) => {
          switch (row.original.status) {
            default:
            case "ACTIVE":
              return <span className={classes.text}>Active</span>;
            case "INACTIVE":
              return <span className={classes.text}>Inactive</span>;
          }
        },
      },
    ],
    [totalPages]
  );


  return (
    <>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
        eShop > Shipping Setting
        </Typography>
      </Box>
      <StatusFilter>
        <li>
            <button
                className={status === "all" ? "active" : ""}
                type="button"
                onClick={statusFilterHandler("all")}
            >
                <Typography variant="p">All
                    ({elementCounts.all})</Typography>
            </button>
        </li>
        <li>
            <button
                className={status === "active" ? "active" : ""}
                type="button"
                onClick={statusFilterHandler("active")}
            >
                <Typography variant="p">
                    Active
                    ({elementCounts.active})
                </Typography>
            </button>
        </li>
        <li>
            <button
                className={status === "inactive" ? "active" : ""}
                type="button"
                onClick={statusFilterHandler("inactive")}
            >
                <Typography variant="p">
                    Inactive
                    ({elementCounts.inactive})
                </Typography>
            </button>
        </li>
      </StatusFilter>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <DragAndDropTable
            globalFilter={globalFilter}
            orderingDisabled={orderingDisabled}
            handleSorting={handleSorting}
            handleSearch={handleSearch}
            searchString={searchString}
            setSearchString={setSearchString}
            fetchData={getShipping}
            data={rows}
            setData={setRows}
            columns={columns}
            bulkDeleteApi={bulkDelete}
            subject="Shipping Region"
            saveOrderingApi={saveOrdering}
            disabled={disabled}
            editDeleteAction={true}
            handleDelete={handleDelete}
            withPagination={true}
            setParentPageSize={setPageSize}
            setParentPageIndex={setPageIndex}
            parentPageIndex={pageIndex}
            totalElements={totalElements}
            totalPages={totalPages}
            addBtnLink={`${match.url}/new`}
            addBtnName="Add New Shipping Region"
          />
        </Grid>
      </Grid>
    </>
  );
}
